/**
 * httpSettingsForm — form state for the HTTP camera settings sheet, extracted
 * from CameraHttpSettingsSheet (item 226) so a group bulk-settings dialog can
 * reuse the same fields without duplicating the bucket-1/bucket-2 logic.
 *
 * Moved verbatim from CameraHttpSettingsSheet.tsx: the `useState<...>`
 * declarations become `HttpSettingsFormState`, the reset `useEffect` body
 * becomes `formStateFromCamera`, and the "Build the bucket-2 dict" block of
 * `buildPayload` becomes `extrasFromFormState`.
 */

import type { BulkSettings, Camera, DesiredExtraSettings } from '@/types'

export const UNCHANGED = '__unchanged__' as const

export type Tristate<T> = T | typeof UNCHANGED

export interface HttpSettingsFormState {
  // ── Bucket-1 form state (typed columns)
  pirInterval: Tristate<number>
  photoSize: Tristate<string>
  videoResolution: Tristate<string>
  videoLength: Tristate<number>

  // ── Bucket-2 form state (one slot per push-only setting; UNCHANGED means "no change")
  // NonNullable: send_mode? is optional so the raw type includes `undefined`,
  // which breaks discriminated-union narrowing on .mode/.count. State is only
  // ever UNCHANGED or a concrete {mode,...} object — never undefined.
  sendMode: NonNullable<DesiredExtraSettings['send_mode']> | typeof UNCHANGED
  workHourMode: 'unchanged' | 'always' | 'window'
  workHourFrom: string
  workHourTo: string
  pirTrigger: Tristate<DesiredExtraSettings['pir_trigger']>
  cameraMode: Tristate<DesiredExtraSettings['camera_mode']>
  smsControl: Tristate<'on' | 'off'>
  timeLapseUnit: 'unchanged' | 'off' | 'minutes' | 'hours'
  timeLapseValue: number
  workDay: boolean[] | null  // null = unchanged
  attachmentSize: Tristate<DesiredExtraSettings['attachment_size']>
  gps: Tristate<'on' | 'off'>
  recycleState: Tristate<'on' | 'off'>

  // ── #044 Phase B: remaining Document C settings
  photoBurst: Tristate<number>
  dateType: Tristate<DesiredExtraSettings['date_type']>
  autoSim: Tristate<DesiredExtraSettings['auto_sim']>
  lockNet: Tristate<DesiredExtraSettings['lock_net']>
  alarmFlash: Tristate<DesiredExtraSettings['alarm_flash']>
  language: Tristate<DesiredExtraSettings['language']>
  beepSound: Tristate<'on' | 'off'>
  timeStamp: Tristate<'on' | 'off'>
  alarmByAi: Tristate<number>
  alarmByMotion: Tristate<'on' | 'off'>
  alarmByCloud: Tristate<'on' | 'off'>
  alarmSound: Tristate<number>
  alarmLight: Tristate<number>
  alarmSoundVolume: Tristate<number>
  ukAttachSize: Tristate<number>
  ukLockMenu: Tristate<number>
  heartbeat: Tristate<number>
  smsTimeDelay: Tristate<number>
  timeZone: Tristate<number>
  timeZoneOffset: Tristate<number>
}

export function emptyFormState(): HttpSettingsFormState {
  return {
    pirInterval: UNCHANGED,
    photoSize: UNCHANGED,
    videoResolution: UNCHANGED,
    videoLength: UNCHANGED,

    sendMode: UNCHANGED,
    workHourMode: 'unchanged',
    workHourFrom: '08:00',
    workHourTo: '18:00',
    pirTrigger: UNCHANGED,
    cameraMode: UNCHANGED,
    smsControl: UNCHANGED,
    timeLapseUnit: 'unchanged',
    timeLapseValue: 30,
    workDay: null,
    attachmentSize: UNCHANGED,
    gps: UNCHANGED,
    recycleState: UNCHANGED,

    photoBurst: UNCHANGED,
    dateType: UNCHANGED,
    autoSim: UNCHANGED,
    lockNet: UNCHANGED,
    alarmFlash: UNCHANGED,
    language: UNCHANGED,
    beepSound: UNCHANGED,
    timeStamp: UNCHANGED,
    alarmByAi: UNCHANGED,
    alarmByMotion: UNCHANGED,
    alarmByCloud: UNCHANGED,
    alarmSound: UNCHANGED,
    alarmLight: UNCHANGED,
    alarmSoundVolume: UNCHANGED,
    ukAttachSize: UNCHANGED,
    ukLockMenu: UNCHANGED,
    heartbeat: UNCHANGED,
    smsTimeDelay: UNCHANGED,
    timeZone: UNCHANGED,
    timeZoneOffset: UNCHANGED,
  }
}

// Reset on open / camera change — moved from CameraHttpSettingsSheet's
// reset `useEffect` body.
export function formStateFromCamera(camera: Camera): HttpSettingsFormState {
  const s = emptyFormState()
  const ex = camera.desired_extra_settings || {}
  s.pirInterval = camera.desired_pir_interval ?? UNCHANGED
  s.photoSize = camera.desired_photo_size || UNCHANGED
  s.videoResolution = camera.desired_video_resolution || UNCHANGED
  s.videoLength = camera.desired_video_length ?? UNCHANGED
  s.sendMode = ex.send_mode ?? UNCHANGED
  if (ex.work_hour) {
    if (ex.work_hour === 'off') {
      s.workHourMode = 'always'
    } else {
      s.workHourMode = 'window'
      const [from, to] = ex.work_hour.split('-')
      s.workHourFrom = from
      s.workHourTo = to
    }
  } else {
    s.workHourMode = 'unchanged'
  }
  s.pirTrigger = ex.pir_trigger ?? UNCHANGED
  s.cameraMode = ex.camera_mode ?? UNCHANGED
  s.smsControl = ex.sms_control ?? UNCHANGED
  if (ex.time_lapse) {
    s.timeLapseUnit = ex.time_lapse.unit
    if (ex.time_lapse.unit !== 'off') s.timeLapseValue = ex.time_lapse.value
  } else {
    s.timeLapseUnit = 'unchanged'
  }
  s.workDay = ex.work_day ? [...ex.work_day].map((c) => c === '1') : null
  s.attachmentSize = ex.attachment_size ?? UNCHANGED
  s.gps = ex.gps ?? UNCHANGED
  s.recycleState = ex.recycle_storage_state ?? UNCHANGED
  s.photoBurst = ex.photo_burst ?? UNCHANGED
  s.dateType = ex.date_type ?? UNCHANGED
  s.autoSim = ex.auto_sim ?? UNCHANGED
  s.lockNet = ex.lock_net ?? UNCHANGED
  s.alarmFlash = ex.alarm_flash ?? UNCHANGED
  s.language = ex.language ?? UNCHANGED
  s.beepSound = ex.beep_sound ?? UNCHANGED
  s.timeStamp = ex.time_stamp ?? UNCHANGED
  s.alarmByAi = ex.alarm_by_ai ?? UNCHANGED
  s.alarmByMotion = ex.alarm_by_motion ?? UNCHANGED
  s.alarmByCloud = ex.alarm_by_cloud ?? UNCHANGED
  s.alarmSound = ex.alarm_sound ?? UNCHANGED
  s.alarmLight = ex.alarm_light ?? UNCHANGED
  s.alarmSoundVolume = ex.alarm_sound_volume ?? UNCHANGED
  s.ukAttachSize = ex.uk_attach_size ?? UNCHANGED
  s.ukLockMenu = ex.uk_lock_menu ?? UNCHANGED
  s.heartbeat = ex.heartbeat ?? UNCHANGED
  s.smsTimeDelay = ex.sms_time_delay ?? UNCHANGED
  s.timeZone = ex.time_zone ?? UNCHANGED
  s.timeZoneOffset = ex.time_zone_offset ?? UNCHANGED
  return s
}

// Build the bucket-2 dict (full replacement on PATCH) — moved from
// CameraHttpSettingsSheet's buildPayload.
export function extrasFromFormState(s: HttpSettingsFormState): DesiredExtraSettings {
  const extras: DesiredExtraSettings = {}
  if (s.sendMode !== UNCHANGED && s.sendMode) extras.send_mode = s.sendMode
  if (s.workHourMode === 'always') extras.work_hour = 'off'
  else if (s.workHourMode === 'window') extras.work_hour = `${s.workHourFrom}-${s.workHourTo}`
  if (s.pirTrigger !== UNCHANGED && s.pirTrigger) extras.pir_trigger = s.pirTrigger
  if (s.cameraMode !== UNCHANGED && s.cameraMode) extras.camera_mode = s.cameraMode
  if (s.smsControl !== UNCHANGED && s.smsControl) extras.sms_control = s.smsControl
  if (s.timeLapseUnit !== 'unchanged') {
    if (s.timeLapseUnit === 'off') extras.time_lapse = { unit: 'off' }
    else if (s.timeLapseUnit === 'minutes') extras.time_lapse = { unit: 'minutes', value: s.timeLapseValue }
    else extras.time_lapse = { unit: 'hours', value: s.timeLapseValue }
  }
  if (s.workDay) extras.work_day = s.workDay.map((b) => (b ? '1' : '0')).join('')
  if (s.attachmentSize !== UNCHANGED && s.attachmentSize) extras.attachment_size = s.attachmentSize
  if (s.gps !== UNCHANGED && s.gps) extras.gps = s.gps
  if (s.recycleState !== UNCHANGED && s.recycleState) extras.recycle_storage_state = s.recycleState
  // #044 Phase B
  if (s.photoBurst !== UNCHANGED) extras.photo_burst = s.photoBurst as 1 | 2 | 3
  if (s.dateType !== UNCHANGED && s.dateType) extras.date_type = s.dateType
  if (s.autoSim !== UNCHANGED && s.autoSim) extras.auto_sim = s.autoSim
  if (s.lockNet !== UNCHANGED && s.lockNet) extras.lock_net = s.lockNet
  if (s.alarmFlash !== UNCHANGED && s.alarmFlash) extras.alarm_flash = s.alarmFlash
  if (s.language !== UNCHANGED && s.language) extras.language = s.language
  if (s.beepSound !== UNCHANGED && s.beepSound) extras.beep_sound = s.beepSound
  if (s.timeStamp !== UNCHANGED && s.timeStamp) extras.time_stamp = s.timeStamp
  if (s.alarmByAi !== UNCHANGED) extras.alarm_by_ai = s.alarmByAi as number
  if (s.alarmByMotion !== UNCHANGED && s.alarmByMotion) extras.alarm_by_motion = s.alarmByMotion
  if (s.alarmByCloud !== UNCHANGED && s.alarmByCloud) extras.alarm_by_cloud = s.alarmByCloud
  if (s.alarmSound !== UNCHANGED) extras.alarm_sound = s.alarmSound as number
  if (s.alarmLight !== UNCHANGED) extras.alarm_light = s.alarmLight as number
  if (s.alarmSoundVolume !== UNCHANGED) extras.alarm_sound_volume = s.alarmSoundVolume as 0 | 1 | 2
  if (s.ukAttachSize !== UNCHANGED) extras.uk_attach_size = s.ukAttachSize as number
  if (s.ukLockMenu !== UNCHANGED) extras.uk_lock_menu = s.ukLockMenu as 0 | 1
  if (s.heartbeat !== UNCHANGED) extras.heartbeat = s.heartbeat as 0 | 1 | 2 | 3
  if (s.smsTimeDelay !== UNCHANGED) extras.sms_time_delay = s.smsTimeDelay as 0 | 1 | 2 | 3 | 4
  if (s.timeZone !== UNCHANGED) extras.time_zone = s.timeZone as number
  if (s.timeZoneOffset !== UNCHANGED) extras.time_zone_offset = s.timeZoneOffset as number
  return extras
}

export function bulkSettingsFromFormState(s: HttpSettingsFormState): BulkSettings {
  const out: BulkSettings = { ...extrasFromFormState(s) }
  if (s.pirInterval !== UNCHANGED) out.pir_interval = s.pirInterval
  if (s.photoSize !== UNCHANGED) out.photo_size = s.photoSize
  if (s.videoResolution !== UNCHANGED) out.video_resolution = s.videoResolution
  if (s.videoLength !== UNCHANGED) out.video_length = s.videoLength
  return out
}

const LABELS: Record<string, string> = {
  pir_interval: 'PIR interval', photo_size: 'Photo size', video_resolution: 'Video resolution',
  video_length: 'Video length', camera_mode: 'Camera mode', pir_trigger: 'PIR sensitivity',
  time_lapse: 'Time lapse', work_hour: 'Work hour', work_day: 'Work days',
  attachment_size: 'Attachment size', recycle_storage_state: 'Recycle storage',
  send_mode: 'Send mode', sms_control: 'SMS control', gps: 'GPS', auto_sim: 'Auto SIM',
  lock_net: 'Network lock', heartbeat: 'Heartbeat', sms_time_delay: 'SMS time delay',
  alarm_by_motion: 'Alarm by motion', alarm_by_cloud: 'Alarm by cloud', alarm_by_ai: 'Alarm by AI',
  alarm_sound: 'Alarm sound', alarm_light: 'Alarm light', alarm_sound_volume: 'Alarm sound volume',
  alarm_flash: 'Alarm flash', language: 'Language', date_type: 'Date format',
  time_zone: 'Time zone', time_zone_offset: 'Time zone offset', photo_burst: 'Photo burst',
  beep_sound: 'Beep sound', time_stamp: 'Timestamp on media', uk_attach_size: 'UK attach size',
  uk_lock_menu: 'UK lock menu',
}

export function settingLabel(key: string): string {
  return LABELS[key] ?? key
}

export function formatSettingValue(key: string, v: unknown): string {
  if (v === null || v === undefined) return '—'
  if (key === 'work_hour' && v === 'off') return 'always on'
  if (key === 'send_mode' && typeof v === 'object') {
    const m = v as { mode: string; count?: number }
    return m.mode === 'off' ? 'off' : `${m.mode} × ${m.count}`
  }
  if (key === 'time_lapse' && typeof v === 'object') {
    const t = v as { unit: string; value?: number }
    return t.unit === 'off' ? 'off' : `${t.value} ${t.unit}`
  }
  return String(v)
}

export function describeBulkSettings(settings: BulkSettings): string[] {
  return Object.entries(settings).map(([k, v]) => `${settingLabel(k)} = ${formatSettingValue(k, v)}`)
}
